import { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Volume2, VolumeX, Maximize2, X } from 'lucide-react';
import { TID } from '../lib/testIds';

const variants = {
  enter: (d) => ({ x: d > 0 ? '100%' : '-100%', opacity: 0.4 }),
  center: { x: 0, opacity: 1 },
  exit: (d) => ({ x: d > 0 ? '-100%' : '100%', opacity: 0.4 }),
};

export default function PortfolioCarousel({ items }) {
  const [[index, dir], setState] = useState([0, 0]);
  const [muted, setMuted] = useState(true);
  const [lightbox, setLightbox] = useState(false);
  const dragging = useRef(false);

  const paginate = useCallback(
    (d) => setState(([i]) => [(i + d + items.length) % items.length, d]),
    [items.length],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') paginate(-1);
      else if (e.key === 'ArrowRight') paginate(1);
      else if (e.key === 'Escape') setLightbox(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [paginate]);

  // Lock background scroll while the lightbox is open
  useEffect(() => {
    document.body.style.overflow = lightbox ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightbox]);

  const item = items[index];

  return (
    <div data-testid={TID.portfolioCarousel} className="relative">
      <div
        className="relative w-full overflow-hidden rounded-2xl border border-white/12 bg-black"
        style={{ aspectRatio: '16 / 9' }}
      >
        <AnimatePresence initial={false} custom={dir}>
          <motion.div
            key={index}
            data-testid={TID.portfolioSlide(index)}
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 260, damping: 32 },
              opacity: { duration: 0.3 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.18}
            onDragStart={() => {
              dragging.current = true;
            }}
            onDragEnd={(e, { offset, velocity }) => {
              const power = Math.abs(offset.x) * velocity.x;
              if (offset.x < -80 || power < -6000) paginate(1);
              else if (offset.x > 80 || power > 6000) paginate(-1);
              setTimeout(() => {
                dragging.current = false;
              }, 60);
            }}
            onClick={() => {
              if (!dragging.current) setLightbox(true);
            }}
            title="Click to view fullscreen"
            className="absolute inset-0 cursor-zoom-in active:cursor-grabbing"
          >
            {item.type === 'video' ? (
              <video
                className="h-full w-full object-cover pointer-events-none"
                src={item.src}
                autoPlay
                muted={muted}
                loop
                playsInline
                preload="metadata"
              />
            ) : (
              <img
                className="h-full w-full object-cover pointer-events-none select-none"
                src={item.src}
                alt={item.title}
                draggable={false}
              />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Counter */}
        <div className="absolute top-5 left-5 z-20 text-[11px] uppercase tracking-[0.3em] text-white/80 bg-black/45 backdrop-blur px-3 py-1.5 border border-white/15 pointer-events-none">
          {String(index + 1).padStart(2, '0')}{' '}
          <span className="text-white/40">/ {String(items.length).padStart(2, '0')}</span>
        </div>

        {/* Top-right controls */}
        <div className="absolute top-5 right-5 z-20 flex items-center gap-2">
          {item.type === 'video' && (
            <button
              data-testid={TID.portfolioMute}
              onClick={() => setMuted((m) => !m)}
              aria-label={muted ? 'Unmute' : 'Mute'}
              className="inline-flex h-10 w-10 items-center justify-center bg-black/50 backdrop-blur border border-white/20 text-white hover:bg-[#D8FF3A] hover:text-black hover:border-[#D8FF3A] transition-colors"
            >
              {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
            </button>
          )}
          <button
            data-testid={TID.portfolioExpand}
            onClick={() => setLightbox(true)}
            aria-label="View fullscreen"
            className="inline-flex h-10 w-10 items-center justify-center bg-black/50 backdrop-blur border border-white/20 text-white hover:bg-[#D8FF3A] hover:text-black hover:border-[#D8FF3A] transition-colors"
          >
            <Maximize2 size={15} />
          </button>
        </div>

        {/* Arrows */}
        <button
          data-testid={TID.portfolioPrev}
          onClick={() => paginate(-1)}
          aria-label="Previous work"
          className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-20 inline-flex h-12 w-12 md:h-14 md:w-14 items-center justify-center rounded-full bg-black/50 backdrop-blur border border-white/20 text-white hover:bg-[#D8FF3A] hover:text-black hover:border-[#D8FF3A] transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <button
          data-testid={TID.portfolioNext}
          onClick={() => paginate(1)}
          aria-label="Next work"
          className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20 inline-flex h-12 w-12 md:h-14 md:w-14 items-center justify-center rounded-full bg-black/50 backdrop-blur border border-white/20 text-white hover:bg-[#D8FF3A] hover:text-black hover:border-[#D8FF3A] transition-colors"
        >
          <ArrowRight size={20} />
        </button>
      </div>

      {/* Dots */}
      <div className="mt-8 flex items-center justify-center gap-2.5">
        {items.map((_, i) => (
          <button
            key={i}
            data-testid={TID.portfolioDot(i)}
            onClick={() => setState([i, i > index ? 1 : -1])}
            aria-label={`Go to item ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === index ? 'w-8 bg-[#D8FF3A]' : 'w-1.5 bg-white/25 hover:bg-white/50'
            }`}
          />
        ))}
      </div>

      {/* Lightbox / fullscreen */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid={TID.portfolioLightbox}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setLightbox(false)}
            className="fixed inset-0 z-[90] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-12"
          >
            {/* Close */}
            <button
              data-testid={TID.portfolioLightboxClose}
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(false);
              }}
              aria-label="Close fullscreen"
              className="absolute top-5 right-5 z-10 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 border border-white/20 text-white hover:bg-[#D8FF3A] hover:text-black hover:border-[#D8FF3A] transition-colors"
            >
              <X size={20} />
            </button>

            {/* Counter */}
            <div className="absolute top-7 left-6 z-10 text-[11px] uppercase tracking-[0.3em] text-white/70 pointer-events-none">
              {String(index + 1).padStart(2, '0')}{' '}
              <span className="text-white/40">/ {String(items.length).padStart(2, '0')}</span>
            </div>

            {/* Media */}
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[86vh] max-w-[92vw] flex items-center justify-center"
            >
              {item.type === 'video' ? (
                <video
                  className="max-h-[86vh] max-w-[92vw] object-contain rounded-lg"
                  src={item.src}
                  autoPlay
                  muted={muted}
                  loop
                  playsInline
                  controls
                />
              ) : (
                <img
                  className="max-h-[86vh] max-w-[92vw] object-contain rounded-lg select-none"
                  src={item.src}
                  alt={item.title}
                  draggable={false}
                />
              )}
            </motion.div>

            {/* Lightbox arrows */}
            <button
              data-testid={TID.portfolioLightboxPrev}
              onClick={(e) => {
                e.stopPropagation();
                paginate(-1);
              }}
              aria-label="Previous"
              className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 z-10 inline-flex h-12 w-12 md:h-14 md:w-14 items-center justify-center rounded-full bg-white/10 border border-white/20 text-white hover:bg-[#D8FF3A] hover:text-black hover:border-[#D8FF3A] transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <button
              data-testid={TID.portfolioLightboxNext}
              onClick={(e) => {
                e.stopPropagation();
                paginate(1);
              }}
              aria-label="Next"
              className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 z-10 inline-flex h-12 w-12 md:h-14 md:w-14 items-center justify-center rounded-full bg-white/10 border border-white/20 text-white hover:bg-[#D8FF3A] hover:text-black hover:border-[#D8FF3A] transition-colors"
            >
              <ArrowRight size={20} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
